import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex/Flex';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton/BrandedButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton/TextButton';
import DescTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/DeviationDescription';
import TextRenderer from '../../TextRenderer';
import CollapsibleBlock from '../../CollapsibleBlock/CollapsibleBlock';
import DeviationDumbView from '../../DeviationViews/Dumbo';
import DeviationLink from '../../Duperbrowse/DeviationLink/DeviationLink';
import { getDeviationUrl } from '../../DeviationViews/_helpers';
import { formatDollars } from '../../utils/formatting';

import s from './OriginalsExpandedShopCard.scss';

const MAX_ORDERS_THUMBS = 3;

interface Props {
  deviation: PapiDeviation;
}

export const OriginalsExpandedShopCard: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const originals = deviation.originals || deviation.extended?.originals;
  if (!originals) {
    return null;
  }

  const {
    basePrice,
    completedOrdersCount = 0,
    completedOrders = [],
  } = originals;
  return (
    <div className={s['root']}>
      <Flex direction="column" gap={8}>
        <DeviationLink deviation={deviation} key={deviation.deviationId}>
          <DeviationDumbView deviation={deviation} width={355} height={215} />
        </DeviationLink>
        {completedOrders.length > 0 && (
          <div className={s['orders']}>
            <div className={s['orders-header']}>
              {t('shop.card.originals.completedOrders')}
              {completedOrders.length > MAX_ORDERS_THUMBS && (
                <TextButton
                  size="small"
                  variant="main"
                  priority="secondary"
                  href={getDeviationUrl(deviation)}
                >
                  {t('shop.card.originals.seeMore')}
                </TextButton>
              )}
            </div>
            <div className={s['orders-thumbs']}>
              {completedOrders
                .slice(0, MAX_ORDERS_THUMBS)
                .map(orderDeviation => (
                  <DeviationLink
                    deviation={orderDeviation}
                    key={orderDeviation.deviationId}
                  >
                    <DeviationDumbView
                      key={orderDeviation.deviationId}
                      deviation={orderDeviation}
                      width={113}
                      height={65}
                    />
                  </DeviationLink>
                ))}
            </div>
          </div>
        )}
      </Flex>

      <div className={s['details']}>
        <div className={s['title']}>{deviation.title}</div>

        <div className={s['price-and-total']}>
          <div className={s['price']}>
            {t('shop.card.originals.basePrice')} {formatDollars(basePrice)}
          </div>

          {completedOrdersCount > 0 && (
            <div className={s['total-orders']}>
              {t('shop.thumb.originals.totalOrders', {
                count: originals.completedOrdersCount,
              })}
            </div>
          )}
        </div>

        <div className={s['actions']}>
          <IconButton
            icon={MailIcon}
            size="large"
            aria-label={t('shop.card.originals.requestButton')}
            variant="originals-ring"
          />
          <BrandedButton variant="originals" size="large">
            {t('shop.card.originals.requestButton')}
          </BrandedButton>
        </div>

        <div className={s['description']}>
          <CollapsibleBlock
            maxHeight={100}
            buttonTextCollapsed={t('shop.card.originals.readMore')}
          >
            <TextRenderer
              className={s['description-text']}
              textContent={deviation.textContent}
              tipTapViewer={DescTiptapViewer}
            />
          </CollapsibleBlock>
        </div>
      </div>
    </div>
  );
};
OriginalsExpandedShopCard.displayName = 'OriginalsExpandedShopCard';

export default OriginalsExpandedShopCard;
